import React, { useState, useEffect } from 'react';
import { Calendar, Plus, Eye, EyeOff, Edit2, Trash2, Check, X } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { User } from '@supabase/supabase-js';
import { listCalendarsCore, createCalendarCore, updateCalendarCore, deleteCalendarCore } from '@gv/core';

interface CalendarItem {
    id: string;
    name: string;
    color: string;
    visible: boolean;
    is_default: boolean;
    user_id: string;
    created_at: string;
}

interface CalendarManagerProps {
    user: User;
    selectedCalendars: string[];
    onCalendarToggle: (calendarId: string) => void;
    onCalendarsChange?: (calendars: CalendarItem[]) => void;
}

// Minimal, modern palette
const CALENDAR_COLORS = [
    '#3B82F6', // Blue
    '#10B981', // Green
    '#F59E0B', // Amber
    '#EF4444', // Red
    '#8B5CF6', // Purple
    '#6B7280'  // Gray
];

export default function CalendarManager({ user, selectedCalendars, onCalendarToggle, onCalendarsChange }: CalendarManagerProps) {
    const [calendars, setCalendars] = useState<CalendarItem[]>([]);
    const [loading, setLoading] = useState(true);
    const [showAddForm, setShowAddForm] = useState(false);
    const [newCalendarName, setNewCalendarName] = useState('');
    const [newCalendarColor, setNewCalendarColor] = useState(CALENDAR_COLORS[0]);
    const [editingId, setEditingId] = useState<string | null>(null);
    const [editName, setEditName] = useState('');
    const [editColor, setEditColor] = useState('');

    // Load calendars
    useEffect(() => {
        loadCalendars();
    }, [user.id]);

    const loadCalendars = async () => {
        try {
            setLoading(true);
            const items = await listCalendarsCore(supabase as any, user.id);

            // Ensure one default exists; if none, create and refetch
            let result = items;
            if (!items.some(c => c.is_default)) {
                try {
                    const created = await createCalendarCore(supabase as any, user.id, {
                        name: 'My Calendar',
                        color: CALENDAR_COLORS[0],
                        visible: true,
                        is_default: true,
                    });
                    result = [...items, created];
                } catch (e: any) {
                    // Handle race: if another instance created the default, just refetch
                    const refetched = await listCalendarsCore(supabase as any, user.id);
                    result = refetched;
                }
            }

            setCalendars(result as any);
            onCalendarsChange?.(result as any);
        } catch (error) {
            console.error('Failed to load calendars:', error);
            setCalendars([]);
            onCalendarsChange?.([]);
        } finally {
            setLoading(false);
        }
    };

    const handleAddCalendar = async () => {
        if (!newCalendarName.trim()) return;

        try {
            const data = await createCalendarCore(supabase as any, user.id, {
                name: newCalendarName.trim(),
                color: newCalendarColor,
                visible: true,
                is_default: false,
            });

            const updated = [...calendars, data as any];
            setCalendars(updated);
            onCalendarsChange?.(updated);

            setNewCalendarName('');
            setNewCalendarColor(CALENDAR_COLORS[0]);
            setShowAddForm(false);
        } catch (error) {
            console.error('Failed to add calendar:', error);
        }
    };

    const handleUpdateCalendar = async (id: string) => {
        if (!editName.trim()) return;

        try {
            const data = await updateCalendarCore(supabase as any, id, { name: editName.trim(), color: editColor });
            const updated = calendars.map(cal => (cal.id === id ? (data as any) : cal));
            setCalendars(updated);
            onCalendarsChange?.(updated);

            setEditingId(null);
            setEditName('');
            setEditColor('');
        } catch (error) {
            console.error('Failed to update calendar:', error);
        }
    };

    const handleDeleteCalendar = async (id: string) => {
        if (calendars.find(c => c.id === id)?.is_default) {
            alert('Cannot delete the default calendar');
            return;
        }

        try {
            await deleteCalendarCore(supabase as any, id);
            const updated = calendars.filter(cal => cal.id !== id);
            setCalendars(updated);
            onCalendarsChange?.(updated);
        } catch (error) {
            console.error('Failed to delete calendar:', error);
        }
    };

    const startEditing = (calendar: CalendarItem) => {
        setEditingId(calendar.id);
        setEditName(calendar.name);
        setEditColor(calendar.color);
    };

    const cancelEditing = () => {
        setEditingId(null);
        setEditName('');
        setEditColor('');
    };

    if (loading) {
        return <div className="text-gray-400 text-sm">Loading calendars...</div>;
    }

    return (
        <div className="bg-gray-900 border border-gray-800 rounded-lg p-3">
            <div className="flex items-center justify-between mb-3">
                <h3 className="text-white font-medium text-sm">My Calendars</h3>
                <button
                    onClick={() => setShowAddForm(true)}
                    className="p-1 rounded hover:bg-gray-800 text-gray-400 hover:text-gray-200"
                    title="Add Calendar"
                >
                    <Plus className="w-4 h-4" />
                </button>
            </div>

            <div className="space-y-2">
                {calendars.map(calendar => (
                    <div key={calendar.id} className="flex items-center gap-2 py-1">
                        <button
                            onClick={() => onCalendarToggle(calendar.id)}
                            className="flex items-center gap-2 flex-1 text-left hover:bg-gray-800 rounded px-2 py-1"
                        >
                            {selectedCalendars.includes(calendar.id) ? (
                                <Eye className="w-4 h-4 text-gray-400" />
                            ) : (
                                <EyeOff className="w-4 h-4 text-gray-500" />
                            )}

                            <div
                                className="w-3 h-3 rounded-full border border-gray-600"
                                style={{ backgroundColor: calendar.color }}
                            />

                            {editingId === calendar.id ? (
                                <div className="flex items-center gap-2 flex-1">
                                    <input
                                        type="text"
                                        value={editName}
                                        onChange={(e) => setEditName(e.target.value)}
                                        className="flex-1 px-2 py-1 bg-gray-800 border border-gray-700 rounded text-gray-200 text-xs"
                                        onKeyDown={(e) => {
                                            if (e.key === 'Enter') handleUpdateCalendar(calendar.id);
                                            if (e.key === 'Escape') cancelEditing();
                                        }}
                                        autoFocus
                                    />
                                    <div className="flex gap-1 flex-wrap">
                                        {CALENDAR_COLORS.map(color => (
                                            <button
                                                key={color}
                                                onClick={() => setEditColor(color)}
                                                className={`w-4 h-4 rounded-full border ${editColor === color ? 'border-white' : 'border-gray-600'}`}
                                                style={{ backgroundColor: color }}
                                            />
                                        ))}
                                    </div>
                                </div>
                            ) : (
                                <span className="text-gray-200 text-sm truncate">
                                    {calendar.name}
                                    {calendar.is_default && <span className="text-gray-500 text-xs ml-1">(default)</span>}
                                </span>
                            )}
                        </button>

                        {editingId === calendar.id ? (
                            <div className="flex items-center gap-1">
                                <button
                                    onClick={() => handleUpdateCalendar(calendar.id)}
                                    className="p-1 rounded hover:bg-gray-800 text-green-400"
                                >
                                    <Check className="w-3 h-3" />
                                </button>
                                <button
                                    onClick={cancelEditing}
                                    className="p-1 rounded hover:bg-gray-800 text-gray-400"
                                >
                                    <X className="w-3 h-3" />
                                </button>
                            </div>
                        ) : (
                            <div className="flex items-center gap-1">
                                <button
                                    onClick={() => startEditing(calendar)}
                                    className="p-1 rounded hover:bg-gray-800 text-gray-400 hover:text-gray-200"
                                >
                                    <Edit2 className="w-3 h-3" />
                                </button>
                                {!calendar.is_default && (
                                    <button
                                        onClick={() => handleDeleteCalendar(calendar.id)}
                                        className="p-1 rounded hover:bg-gray-800 text-gray-400 hover:text-red-400"
                                    >
                                        <Trash2 className="w-3 h-3" />
                                    </button>
                                )}
                            </div>
                        )}
                    </div>
                ))}
            </div>

            {showAddForm && (
                <div className="mt-3 p-2 bg-gray-800 rounded border border-gray-700">
                    <input
                        type="text"
                        placeholder="Calendar name"
                        value={newCalendarName}
                        onChange={(e) => setNewCalendarName(e.target.value)}
                        className="w-full px-2 py-1 bg-gray-900 border border-gray-700 rounded text-gray-200 text-sm mb-2"
                        onKeyDown={(e) => {
                            if (e.key === 'Enter') handleAddCalendar();
                            if (e.key === 'Escape') setShowAddForm(false);
                        }}
                        autoFocus
                    />

                    <div className="flex items-center justify-between">
                        <div className="flex gap-1 flex-wrap">
                            {CALENDAR_COLORS.map(color => (
                                <button
                                    key={color}
                                    onClick={() => setNewCalendarColor(color)}
                                    className={`w-5 h-5 rounded-full border ${newCalendarColor === color ? 'border-white' : 'border-gray-600'}`}
                                    style={{ backgroundColor: color }}
                                />
                            ))}
                        </div>

                        <div className="flex items-center gap-2">
                            <button
                                onClick={() => setShowAddForm(false)}
                                className="px-2 py-1 rounded border border-gray-700 text-gray-300 text-xs"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleAddCalendar}
                                disabled={!newCalendarName.trim()}
                                className="px-2 py-1 rounded bg-blue-600 border border-blue-500 text-white text-xs disabled:opacity-50"
                            >
                                Add
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}